import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import Layout from '../components/Layout';
import OperationGroupDialog from '../components/OperationGroupDialog';
import StoreDialog from '../components/StoreDialog';
import { useAdminScope } from '../contexts/AdminScopeContext';
import { OperationGroup, OperationGroupFormData } from '../models/OperationGroup';
import { Store, StoreFormData } from '../models/Store';
import { operationGroupService } from '../services/operationGroupService';
import { storeService } from '../services/storeService';

const Stores: React.FC = () => {
  const { refreshScopeData, stores, operationGroups, loading } = useAdminScope();
  const [error, setError] = useState<string | null>(null);

  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [storeToDelete, setStoreToDelete] = useState<Store | null>(null);

  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<OperationGroup | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<OperationGroup | null>(null);

  const storesByGroupId = useMemo(() => {
    const map = new Map<string, Store[]>();
    for (const store of stores) {
      const groupId = store.operationGroupId || '__ungrouped__';
      const current = map.get(groupId) || [];
      current.push(store);
      map.set(groupId, current);
    }
    return map;
  }, [stores]);

  const reloadAfterMutation = async () => {
    setError(null);
    await refreshScopeData();
  };

  const handleCreateStore = async (storeData: StoreFormData): Promise<string> => {
    const created = await storeService.createStore(storeData);
    await reloadAfterMutation();
    return created.id;
  };

  const handleUpdateStore = async (storeData: StoreFormData): Promise<string> => {
    if (!selectedStore) {
      throw new Error('Aucun magasin sélectionné');
    }
    await storeService.updateStore(selectedStore.id, storeData);
    await reloadAfterMutation();
    return selectedStore.id;
  };

  const handleDeleteStore = async () => {
    if (!storeToDelete) {
      return;
    }

    try {
      await storeService.deleteStore(storeToDelete.id);
      setStoreToDelete(null);
      await reloadAfterMutation();
    } catch (err: any) {
      setError(`Erreur lors de la suppression du magasin: ${err.message}`);
    }
  };

  const handleSetAsDeliveryResponsible = async (store: Store) => {
    try {
      await storeService.setDeliveryResponsibleStore(store.id);
      await reloadAfterMutation();
    } catch (err: any) {
      setError(`Erreur lors de la définition du magasin responsable des livraisons: ${err.message}`);
    }
  };

  const handleCreateGroup = async (groupData: OperationGroupFormData): Promise<string> => {
    const created = await operationGroupService.createGroup(groupData);
    await reloadAfterMutation();
    return created.id;
  };

  const handleUpdateGroup = async (groupData: OperationGroupFormData): Promise<string> => {
    if (!selectedGroup) {
      throw new Error('Aucun groupe sélectionné');
    }
    await operationGroupService.updateGroup(selectedGroup.id, groupData);
    await reloadAfterMutation();
    return selectedGroup.id;
  };

  const handleDeleteGroup = async () => {
    if (!groupToDelete) {
      return;
    }

    try {
      await operationGroupService.deleteGroup(groupToDelete.id);
      setGroupToDelete(null);
      await reloadAfterMutation();
    } catch (err: any) {
      setError(`Erreur lors de la suppression du groupe: ${err.message}`);
    }
  };

  const saveStore = async (storeData: StoreFormData): Promise<string> => {
    if (selectedStore) {
      return handleUpdateStore(storeData);
    }
    return handleCreateStore(storeData);
  };

  const saveGroup = async (groupData: OperationGroupFormData): Promise<string> => {
    if (selectedGroup) {
      return handleUpdateGroup(groupData);
    }
    return handleCreateGroup(groupData);
  };

  return (
    <Layout title="Magasins & Groupes">
      <Container maxWidth="xl">
        <Box sx={{ mt: 4, mb: 4 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Box>
              <Typography variant="h4" component="h1" gutterBottom>
                Magasins & Groupes Opérationnels
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Les groupes partagent la logistique livreurs et les règles de livraison. Les
                magasins portent ensuite le catalogue, la visibilité online et la disponibilité POS.
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedGroup(null);
                  setGroupDialogOpen(true);
                }}
              >
                Ajouter un groupe
              </Button>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setSelectedStore(null);
                  setStoreDialogOpen(true);
                }}
              >
                Ajouter un magasin
              </Button>
            </Stack>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              <AlertTitle>Erreur</AlertTitle>
              {error}
            </Alert>
          )}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={4}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Groupes opérationnels
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Un groupe réunit les magasins qui partagent les mêmes livreurs et les mêmes
                  paramètres de livraison.
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Groupe</TableCell>
                        <TableCell>Ville</TableCell>
                        <TableCell>Magasins rattachés</TableCell>
                        <TableCell>Responsable livraison</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {operationGroups.map((group) => {
                        const scopedStores = storesByGroupId.get(group.id) || [];
                        const responsibleStore =
                          scopedStores.find((store) => store.id === group.deliveryResponsibleStoreId) ||
                          null;

                        return (
                          <TableRow key={group.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>
                                {group.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {group.id}
                              </Typography>
                            </TableCell>
                            <TableCell>{group.city || <em>Non défini</em>}</TableCell>
                            <TableCell>{scopedStores.length}</TableCell>
                            <TableCell>{responsibleStore?.name || <em>Non défini</em>}</TableCell>
                            <TableCell>
                              <Chip
                                label={group.isActive ? 'Actif' : 'Inactif'}
                                color={group.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Modifier">
                                <IconButton
                                  onClick={() => {
                                    setSelectedGroup(group);
                                    setGroupDialogOpen(true);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton
                                  onClick={() => setGroupToDelete(group)}
                                  sx={{
                                    color: '#dc2626',
                                    '&:hover': {
                                      backgroundColor: 'rgba(220, 38, 38, 0.1)',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Magasins
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  La visibilité online, le pickup et la livraison sont désormais gérés magasin par
                  magasin.
                </Typography>

                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Magasin</TableCell>
                        <TableCell>Groupe</TableCell>
                        <TableCell>Adresse</TableCell>
                        <TableCell>Contact</TableCell>
                        <TableCell>Canaux</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stores.map((store) => {
                        const group =
                          operationGroups.find((operationGroup) => operationGroup.id === store.operationGroupId) ||
                          null;

                        return (
                          <TableRow key={store.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={700}>
                                {store.name}
                              </Typography>
                              {store.isDeliveryResponsible && (
                                <Chip
                                  icon={<LocalShippingIcon />}
                                  label="Responsable livraison"
                                  size="small"
                                  color="primary"
                                  sx={{ ml: 1 }}
                                />
                              )}
                            </TableCell>
                            <TableCell>{group?.name || <em>Non défini</em>}</TableCell>
                            <TableCell>{store.address}</TableCell>
                            <TableCell>
                              {store.phone && <div>{store.phone}</div>}
                              {store.email && <div>{store.email}</div>}
                              {!store.phone && !store.email && <em>Non défini</em>}
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip
                                  label={store.isVisibleOnline ? 'Online' : 'Offline'}
                                  size="small"
                                  color={store.isVisibleOnline ? 'success' : 'default'}
                                />
                                <Chip
                                  label={store.isPickupEnabled ? 'Pickup' : 'Pickup off'}
                                  size="small"
                                  variant="outlined"
                                />
                                <Chip
                                  label={store.isDeliveryEnabled ? 'Livraison' : 'Livraison off'}
                                  size="small"
                                  variant="outlined"
                                />
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={store.isActive ? 'Actif' : 'Inactif'}
                                color={store.isActive ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip title="Modifier">
                                <IconButton
                                  onClick={() => {
                                    setSelectedStore(store);
                                    setStoreDialogOpen(true);
                                  }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton
                                  onClick={() => setStoreToDelete(store)}
                                  sx={{
                                    color: '#dc2626',
                                    '&:hover': {
                                      backgroundColor: 'rgba(220, 38, 38, 0.1)',
                                      transform: 'scale(1.1)',
                                    },
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                              {!store.isDeliveryResponsible && (
                                <Tooltip title="Définir comme responsable livraison">
                                  <IconButton onClick={() => handleSetAsDeliveryResponsible(store)}>
                                    <LocalShippingIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          )}
        </Box>

        {storeDialogOpen && (
          <StoreDialog
            open={storeDialogOpen}
            onClose={() => {
              setStoreDialogOpen(false);
              setSelectedStore(null);
            }}
            onSave={saveStore}
            operationGroups={operationGroups}
            store={selectedStore || undefined}
            title={selectedStore ? 'Modifier le magasin' : 'Ajouter un magasin'}
          />
        )}

        {groupDialogOpen && (
          <OperationGroupDialog
            open={groupDialogOpen}
            onClose={() => {
              setGroupDialogOpen(false);
              setSelectedGroup(null);
            }}
            onSave={saveGroup}
            group={selectedGroup || undefined}
          />
        )}

        <Dialog open={!!storeToDelete} onClose={() => setStoreToDelete(null)}>
          <DialogTitle>Confirmer la suppression</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer le magasin "{storeToDelete?.name}" ?
              {storeToDelete?.isDeliveryResponsible && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Ce magasin est actuellement responsable des livraisons pour son groupe. Un autre
                  magasin actif du groupe sera sélectionné si possible.
                </Alert>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStoreToDelete(null)}>Annuler</Button>
            <Button onClick={handleDeleteStore} color="error">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!groupToDelete} onClose={() => setGroupToDelete(null)}>
          <DialogTitle>Confirmer la suppression du groupe</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Êtes-vous sûr de vouloir supprimer le groupe "{groupToDelete?.name}" ?
              Cette action sera refusée s’il reste des magasins rattachés.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setGroupToDelete(null)}>Annuler</Button>
            <Button onClick={handleDeleteGroup} color="error">
              Supprimer
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default Stores;
